import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { BookOpen, Plane, Home, Plus, LogOut, Settings, ChevronRight } from 'lucide-react';
import { tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { isAuthenticated, getStoredUser, clearAuthData, getModuleColor, getModuleName, getModulePath, formatDate } from '@/lib/utils';

const moduleIcons = {
  notes: BookOpen,
  travel: Plane,
  home: Home,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    const storedUser = getStoredUser();
    setUser(storedUser);
    loadArtifacts();
  }, [navigate]);

  const loadArtifacts = async () => {
    try {
      const response = await tokenApi.getUserArtifacts();
      setArtifacts(response.data);
    } catch (error) {
      toast.error('Failed to load artifacts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getModuleRoute = (artifact) => {
    return getModulePath(artifact.module_type, artifact.token_id);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <span className="font-bold text-xl tracking-tight">MEMORA</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.email}</span>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">My Artifacts</h1>
          <p className="text-slate-600 mb-8">Your connected MEMORA devices and modules</p>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="w-12 h-12 bg-slate-200 rounded-xl mb-4" />
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-slate-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : artifacts.length === 0 ? (
            <motion.div 
              className="bg-white rounded-2xl p-12 text-center border border-slate-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Plus className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No artifacts yet</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Scan a MEMORA artifact's QR code or NFC tag to activate it and add it to your collection.
              </p>
              <Button className="btn-primary" data-testid="scan-artifact-btn">
                Scan Artifact
              </Button>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artifacts.map((artifact, index) => {
                const Icon = moduleIcons[artifact.module_type] || BookOpen;
                const color = getModuleColor(artifact.module_type);
                const name = getModuleName(artifact.module_type);
                
                return (
                  <motion.div
                    key={artifact.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={getModuleRoute(artifact)} data-testid={`artifact-card-${artifact.token_id}`}>
                      <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                          style={{ backgroundColor: `${color}15` }}
                        >
                          <Icon className="w-7 h-7" style={{ color }} />
                        </div>
                        
                        <h3 className="text-lg font-semibold text-slate-900 mb-1">{name}</h3>
                        <p className="text-sm font-mono text-slate-500 mb-3">{artifact.token_id}</p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            Activated {formatDate(artifact.activated_at)}
                          </span>
                          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
              
              {/* Add new artifact card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: artifacts.length * 0.1 }}
              >
                <div className="bg-slate-50 rounded-2xl p-6 border-2 border-dashed border-slate-200 hover:border-slate-300 transition-colors cursor-pointer h-full flex flex-col items-center justify-center min-h-[200px]">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Plus className="w-7 h-7 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-600 mb-1">Add Artifact</h3>
                  <p className="text-sm text-slate-400 text-center">Scan a QR code or NFC tag</p>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>

        {/* Quick Stats */}
        {artifacts.length > 0 && (
          <motion.section
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4">Quick Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <p className="text-3xl font-bold text-slate-900">{artifacts.length}</p>
                <p className="text-sm text-slate-500">Active Artifacts</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <p className="text-3xl font-bold text-blue-600">
                  {artifacts.filter(a => a.module_type === 'notes').length}
                </p>
                <p className="text-sm text-slate-500">Notes Modules</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <p className="text-3xl font-bold text-teal-600">
                  {artifacts.filter(a => a.module_type === 'travel').length}
                </p>
                <p className="text-sm text-slate-500">Travel Tags</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <p className="text-3xl font-bold text-emerald-600">
                  {artifacts.filter(a => a.module_type === 'home').length}
                </p>
                <p className="text-sm text-slate-500">Home Hubs</p>
              </div>
            </div>
          </motion.section>
        )}
      </main>
    </div>
  );
}
